import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, Modal, Platform, Pressable, ScrollView, StyleSheet, TextInput, View } from "react-native";
import {
  ArrowLeftIcon,
  CaretDownIcon,
  CaretUpIcon,
  FileTextIcon,
  LightbulbIcon,
  SlidersHorizontalIcon,
  SparkleIcon,
  TrashIcon,
  ArrowRightIcon,
  WarningIcon,
  XIcon,
} from "@phosphor-icons/react";
import { ArchiveIcon, FilePenLineIcon, RotateCwIcon } from "lucide-react";
import { ContentPicker } from "../../components/common/general/ContentPicker/ContentPicker";
import type { ExternalPickedItem, PickedItem } from "../../components/common/general/ContentPicker/ContentPicker.types";
import { useNavigation, useRoute } from "@react-navigation/native";
import type { NavigationProp, RouteProp } from "@react-navigation/native";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useDebounce } from "use-debounce";
import {
  ConnectorType,
  CourseIntakeMaterialAnalysisStatus,
  CourseIntakeMaterialRetentionPolicy,
  CourseModernizerSourceType,
  ExternalSourceChunkingStrategyInput,
  LearningOutcomeApprovalStatus,
  LearningOutcomeExtractionJobStatus,
  LearningOutcomeSourceKind,
  StoragePathType,
  useApproveLearningOutcomeSetMutation,
  useCreateLearningOutcomeMutation,
  useDeleteLearningOutcomeMutation,
  useGenerateLearningOutcomeCandidatesFromIntakeSignalsMutation,
  useGenerateLearningOutcomeSetMutation,
  useGetLearningOutcomeCourseContentExtractionStatusQuery,
  useGetCourseModernizerIntakeQuery,
  useRevertLearningOutcomeAiRevisionMutation,
  useSaveCourseModernizerIntakeDraftMutation,
  useReviseLearningOutcomeWithAiMutation,
  useStartLearningOutcomeCourseContentExtractionMutation,
  useSubmitCourseModernizerIntakeMutation,
  useUpdateLearningOutcomeMutation,
} from "@axio/graphql/generated/graphql";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import CustomModal from "../../components/common/general/CustomModal/CustomModal";
import type { RootStackParamList } from "../../navigation/navigation.types";
import { useToast } from "../../contexts/Toast/ToastProvider";
import CourseModernizerLearningOutcomes from "../../components/adminDashboard/CourseModernizerLearningOutcomes/CourseModernizerLearningOutcomes";
import { DEFAULT_CONTEXT } from "../../utils/contentPicker/contentPickerContextPresets";
import { useAttachExternalContent } from "../../hooks/contentPicker/useAttachExternalContent";
import { useExternalIngestionStatus } from "../../hooks/contentPicker/useExternalIngestionStatus";
import useFileUpload from "../../hooks/useFileUpload";
import {
  convertExternalPickedItemsToAttachmentInput,
  pairExternalItemsWithQueuedJobs,
} from "../../utils/contentPicker/externalItemAdapter";
import { colors, radii, spacing, typographyScale } from "../../styles/design-system";

type IntakeRoute = RouteProp<RootStackParamList, "Course Modernizer Intake">;
type Intake = NonNullable<
  NonNullable<
    NonNullable<ReturnType<typeof useGetCourseModernizerIntakeQuery>["data"]>["getCourseModernizerIntake"]
  >["data"]
>;
type IntakeOutcome = NonNullable<Intake["learningOutcomes"]>[number];

type IntakeMaterial = {
  id: string;
  fileId: string;
  position: number | null;
  title: string;
  fileUrl?: string | null;
  fileStatus?: string | null;
  sourceConnectorType?: ConnectorType | null;
  instructions?: string | null;
  retentionPolicy: CourseIntakeMaterialRetentionPolicy;
  summary?: string | null;
  analysisStatus?: CourseIntakeMaterialAnalysisStatus | null;
  analysisError?: string | null;
  extractionOutput?: unknown | null;
};

type IntakeAnalysisStatus = "QUEUED" | "PROCESSING" | "COMPLETED" | "FAILED" | "UNSUPPORTED";

type PendingExternalMaterial = {
  id: string;
  name: string;
  externalJobId: string;
  pickerKey: string;
  status: "pending" | "processing" | "failed";
  message?: string | null;
};

type IntakeFormValues = {
  visionIntent: string;
  audienceNotes: string;
  signatureStyleTags: string[];
  signatureStyleOther: string;
  intakeMaterials: IntakeMaterial[];
};

type SourceActionState = {
  kind: "ai" | "upload" | "revise";
  status: "idle" | "loading" | "empty" | "error";
  message: string;
} | null;

type PendingAiGeneration = {
  instruction: string;
  targetCount?: number;
};

const DEFAULT_INTAKE_FORM_VALUES: IntakeFormValues = {
  visionIntent: "",
  audienceNotes: "",
  signatureStyleTags: [],
  signatureStyleOther: "",
  intakeMaterials: [],
};

const STYLE_TAGS = [
  "Case-based learning",
  "Socratic questioning",
  "Project-based",
  "Lecture-heavy",
  "Flipped classroom",
  "Discussion-driven",
  "Lab / hands-on",
  "Real-world applications",
  "Storytelling / narrative",
];

const CONNECTOR_SOURCE_LABELS: Record<string, string> = {
  [ConnectorType.Concourse]: "Concourse",
  [ConnectorType.GoogleDrive]: "Google Drive",
  [ConnectorType.OneDrive]: "OneDrive",
  [ConnectorType.Kaltura]: "Kaltura",
  [ConnectorType.LegacyConnector]: "Connector",
};

const getOutcomeLabel = (position: number) => `LO ${position}`;

const getErrorMessage = (error: unknown): string => (error instanceof Error ? error.message : "Something went wrong");

const getAiBadgeLabel = (sourceKind?: LearningOutcomeSourceKind | string | null): string | null => {
  if (sourceKind === LearningOutcomeSourceKind.AiGenerated || sourceKind === "AI_GENERATED") {
    return "AI-generated";
  }
  if (
    sourceKind === LearningOutcomeSourceKind.AiRevised ||
    sourceKind === LearningOutcomeSourceKind.PdfDocExtracted ||
    sourceKind === "AI_REVISED" ||
    sourceKind === "PDF_DOC_EXTRACTED"
  ) {
    return "AI-revised";
  }
  return null;
};

const getOutcomeBadgeLabel = (outcome: Pick<IntakeOutcome, "originalText" | "sourceKind">): string | null =>
  getAiBadgeLabel(outcome.sourceKind) ?? (outcome.originalText?.trim() ? "Edited" : null);

const hasRevertableOutcomeText = (outcome: Pick<IntakeOutcome, "originalText" | "sourceKind">): boolean =>
  outcome.sourceKind !== LearningOutcomeSourceKind.PdfDocExtracted &&
  outcome.sourceKind !== "PDF_DOC_EXTRACTED" &&
  Boolean(outcome.originalText?.trim());

const hasAiGeneratedOutcome = (outcome: Pick<IntakeOutcome, "isAIDrafted" | "sourceKind">): boolean =>
  outcome.isAIDrafted === true ||
  outcome.sourceKind === LearningOutcomeSourceKind.AiGenerated ||
  outcome.sourceKind === "AI_GENERATED";

const getManualEditSourceProvenance = (
  outcome: Pick<IntakeOutcome, "isAIDrafted" | "sourceKind" | "sourceProvenance" | "text">
) => ({
  previousText: outcome.text,
  previousSourceKind: outcome.sourceKind ?? null,
  previousIsAIDrafted: outcome.isAIDrafted ?? false,
  previousProvenance: outcome.sourceProvenance ?? null,
});

const getDraftTextKey = (
  draft: Pick<IntakeFormValues, "audienceNotes" | "signatureStyleOther" | "visionIntent">
): string =>
  JSON.stringify({
    audienceNotes: draft.audienceNotes,
    signatureStyleOther: draft.signatureStyleOther,
    visionIntent: draft.visionIntent,
  });

const normalizeIntakeMaterials = (value: unknown): IntakeMaterial[] => {
  if (!Array.isArray(value)) return [];
  return value
    .flatMap((item) => {
      if (!item || typeof item !== "object" || !("id" in item) || !("file" in item)) return [];
      const material = item as {
        file?: {
          externalSourceProvenance?: { connectorType?: ConnectorType | null } | null;
          fileName?: string | null;
          fileURL?: string | null;
          id?: string | null;
          status?: string | null;
        } | null;
        id?: string | null;
        modernizerInstructions?: string | null;
        position?: number | null;
        retentionPolicy?: CourseIntakeMaterialRetentionPolicy | null;
        summary?: string | null;
        analysisStatus?: CourseIntakeMaterialAnalysisStatus | null;
        analysisError?: string | null;
      };
      if (!material.id || !material.file?.id) return [];
      return [
        {
          id: material.id,
          fileId: material.file.id,
          position: material.position ?? null,
          title: material.file.fileName ?? "Untitled material",
          fileUrl: material.file.fileURL ?? null,
          fileStatus: material.file.status ?? null,
          sourceConnectorType: material.file.externalSourceProvenance?.connectorType ?? null,
          instructions: material.modernizerInstructions ?? null,
          retentionPolicy: material.retentionPolicy ?? CourseIntakeMaterialRetentionPolicy.Retain,
          summary: material.summary ?? null,
          analysisStatus: material.analysisStatus ?? null,
          analysisError: material.analysisError ?? null,
          extractionOutput: material.extractionOutput ?? null,
        },
      ];
    })
    .sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER));
};

const toDraftIntakeMaterials = (materials: IntakeMaterial[]) =>
  materials.map((material, index) => ({
    fileId: material.fileId,
    position: index + 1,
    modernizerInstructions: material.instructions ?? null,
    retentionPolicy: material.retentionPolicy,
  }));

const getPickedItemKey = (item: PickedItem): string => {
  if (item.kind === "local") {
    return item.uploadedFileId
      ? `local-file:${item.uploadedFileId}`
      : `local-pending:${item.name}:${item.sizeBytes ?? ""}:${item.mimeType ?? ""}`;
  }
  if (item.kind === "external") {
    return `external:${item.connectorType}:${item.externalId}`;
  }
  return `link:${item.url}`;
};

const DOCUMENT_PICKER_MIME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/markdown",
  "text/x-markdown",
]);

const DOCUMENT_PICKER_EXTENSIONS = new Set(["pdf", "doc", "docx", "txt", "md", "ppt", "pptx"]);
const INTAKE_MATERIALS_FORMAT_HINT = ".PDF, .DOCX, .DOC, .TXT, .MD, .PPTX, .PPT (max 25MB)";
const LEARNING_OUTCOME_EXTRACTION_TIMEOUT_MS = 120_000;
const OUTCOME_UPLOAD_MIME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);
const OUTCOME_UPLOAD_EXTENSIONS = new Set(["pdf", "doc", "docx"]);
const DOCUMENT_PICKER_ACCEPT = [
  ".pdf",
  ".doc",
  ".docx",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
].join(",");

const getFileExtension = (fileName: string): string => fileName.toLowerCase().trim().split(".").pop() ?? "";

const getAllowedDocumentFileMimeType = (file: File): string | null => {
  const mimeType = file.type.toLowerCase().trim();
  if (mimeType && OUTCOME_UPLOAD_MIME_TYPES.has(mimeType)) {
    return mimeType;
  }

  const extension = getFileExtension(file.name);
  if (!OUTCOME_UPLOAD_EXTENSIONS.has(extension)) {
    return null;
  }

  if (extension === "pdf") return "application/pdf";
  if (extension === "doc") return "application/msword";
  return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
};

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read source document."));
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Failed to read source document."));
    };
    reader.readAsDataURL(file);
  });

const isDocumentPickerItem = (item: PickedItem): boolean => {
  if (item.kind === "link") return false;
  const mimeType = item.mimeType?.toLowerCase().trim();
  if (mimeType && DOCUMENT_PICKER_MIME_TYPES.has(mimeType)) {
    return true;
  }

  const fileName = item.name?.toLowerCase().trim();
  if (!fileName || !fileName.includes(".")) {
    return false;
  }

  const extension = getFileExtension(fileName);
  return DOCUMENT_PICKER_EXTENSIONS.has(extension);
};

const toIntakeFormValues = (intake: Intake): IntakeFormValues => ({
  audienceNotes: intake.audienceNotes ?? "",
  intakeMaterials: normalizeIntakeMaterials(intake.intakeMaterials),
  signatureStyleOther: intake.signatureStyleOther ?? "",
  signatureStyleTags: (intake.signatureStyleTags ?? []).filter((tag): tag is string => Boolean(tag)),
  visionIntent: intake.visionIntent ?? "",
});

function CourseModernizerIntakeView() {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const route = useRoute<IntakeRoute>();
  const toast = useToast();
  const { externalCourseId } = route.params;
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [editingMaterialId, setEditingMaterialId] = useState<string | null>(null);
  const [instructionsDraft, setInstructionsDraft] = useState("");
  const [isInstructionsFocused, setIsInstructionsFocused] = useState(false);
  const [pendingExternalMaterials, setPendingExternalMaterials] = useState<PendingExternalMaterial[]>([]);
  const [externalJobIds, setExternalJobIds] = useState<string[]>([]);
  const [sourceActionState, setSourceActionState] = useState<SourceActionState>(null);
  const [saveState, setSaveState] = useState<"saved" | "saving" | "error">("saved");
  const [retryingMaterialId, setRetryingMaterialId] = useState<string | null>(null);
  const [pendingAiGeneration, setPendingAiGeneration] = useState<PendingAiGeneration | null>(null);
  const [isUploadOutcomeWarningOpen, setIsUploadOutcomeWarningOpen] = useState(false);
  const isGeneratingUploadOutcomesRef = useRef(false);
  const lastUploadExtractionSignatureRef = useRef<string | null>(null);
  const hydratedDraftTextKeyRef = useRef<string | null>(null);
  const outcomeSourceFileInputRef = useRef<HTMLInputElement | null>(null);
  const currentOutcomeUploadFileIdsRef = useRef<Set<string>>(new Set());
  const prevPickedKeysRef = useRef<Set<string>>(new Set());
  const processedExternalJobsRef = useRef<Set<string>>(new Set());
  const pickerMaterialKeysRef = useRef<Map<string, string>>(new Map());
  const externalJobPickerKeysRef = useRef<Map<string, string>>(new Map());
  const externalJobConnectorTypesRef = useRef<Map<string, ConnectorType>>(new Map());
  const { control, getValues, handleSubmit, reset, setValue } = useForm<IntakeFormValues>({
    defaultValues: DEFAULT_INTAKE_FORM_VALUES,
  });
  const watchedDraftText = useWatch({
    control,
    name: ["visionIntent", "audienceNotes", "signatureStyleOther"],
  });
  const visionIntent = watchedDraftText[0] ?? "";
  const audienceNotes = watchedDraftText[1] ?? "";
  const signatureStyleOther = watchedDraftText[2] ?? "";
  const signatureStyleTags = useWatch({ control, name: "signatureStyleTags" }) ?? [];
  const intakeMaterials = useWatch({ control, name: "intakeMaterials" }) ?? [];
  const [debouncedVisionIntent] = useDebounce(visionIntent, 700);
  const [debouncedAudienceNotes] = useDebounce(audienceNotes, 700);
  const [debouncedSignatureStyleOther] = useDebounce(signatureStyleOther, 700);

  const { data, loading, error, refetch } = useGetCourseModernizerIntakeQuery({
    variables: { input: { sourceType: CourseModernizerSourceType.Lms, sourceId: externalCourseId } },
    fetchPolicy: "network-only",
  });
  const [createLearningOutcome, { loading: isCreatingOutcome }] = useCreateLearningOutcomeMutation();
  const [updateLearningOutcome] = useUpdateLearningOutcomeMutation();
  const [deleteLearningOutcome] = useDeleteLearningOutcomeMutation();
  const [generateLearningOutcomeSet, { loading: isGeneratingOutcomes }] = useGenerateLearningOutcomeSetMutation();
  const [reviseLearningOutcomeWithAI, { loading: isRevisingOutcome }] = useReviseLearningOutcomeWithAiMutation();
  const [revertLearningOutcomeAIRevision, { loading: isRevertingOutcome }] =
    useRevertLearningOutcomeAiRevisionMutation();
  const [approveLearningOutcomeSet, { loading: isApproving }] = useApproveLearningOutcomeSetMutation();
  const [saveCourseModernizerIntakeDraft] = useSaveCourseModernizerIntakeDraftMutation();
  const [generateLearningOutcomeCandidatesFromIntakeSignals] =
    useGenerateLearningOutcomeCandidatesFromIntakeSignalsMutation();
  const [startLearningOutcomeCourseContentExtraction] = useStartLearningOutcomeCourseContentExtractionMutation();
  const [learningOutcomeExtractionJobId, setLearningOutcomeExtractionJobId] = useState<string | null>(null);
  const { data: learningOutcomeExtractionStatusData, error: learningOutcomeExtractionStatusError } =
    useGetLearningOutcomeCourseContentExtractionStatusQuery({
      variables: { jobId: learningOutcomeExtractionJobId ?? "" },
      skip: !learningOutcomeExtractionJobId,
      fetchPolicy: "no-cache",
      pollInterval: learningOutcomeExtractionJobId ? 2000 : 0,
      notifyOnNetworkStatusChange: true,
    });
  const autoExtractAttemptedIntakeIdsRef = useRef<Set<string>>(new Set());
  const pendingDroppedFilesRef = useRef<File[] | null>(null);
  const { attachExternal } = useAttachExternalContent();
  const { jobStatuses } = useExternalIngestionStatus(externalJobIds);
  const { handleServerUpload: uploadOutcomeSourceFile } = useFileUpload({
    storagePath: StoragePathType.AdminDocuments,
    onError: (message) => toast?.showToast({ message: message ?? "Failed to upload source document", type: "error" }),
  });
  const [submitCourseModernizerIntake, { loading: isSubmittingModernizerIntake }] =
    useSubmitCourseModernizerIntakeMutation();

  const intake = data?.getCourseModernizerIntake?.data ?? null;
  const readiness = data?.getCourseModernizerIntake?.readiness ?? null;
  const outcomes = useMemo(
    () => [...(intake?.learningOutcomes ?? [])].sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0)),
    [intake?.learningOutcomes]
  );
  const outcomeApprovalStatus = intake?.learningOutcomeApprovalStatus ?? LearningOutcomeApprovalStatus.NotApproved;
  const isOutcomeApproved =
    outcomeApprovalStatus === LearningOutcomeApprovalStatus.ApprovedWithOutcomes ||
    outcomeApprovalStatus === LearningOutcomeApprovalStatus.ApprovedEmpty;
  const isReadyToModernize = readiness?.ready === true;
  const isOutcomeSourceLoading = sourceActionState?.status === "loading";
  const isOutcomeGenerationPending = isGeneratingOutcomes || isOutcomeSourceLoading;
  const isModernizeDisabled = !isReadyToModernize || isSubmittingModernizerIntake || isOutcomeGenerationPending;
  const footerReadinessMessage =
    readiness?.ready === true
      ? (readiness.warnings[0]?.message ??
        "We'll generate a gap analysis once modernization completes. Everything captured here is editable in Course Details after the run.")
      : (readiness?.reasons[0]?.message ?? "Complete the required intake gates before submitting.");
  const editingMaterial = useMemo(
    () => intakeMaterials.find((material) => material.id === editingMaterialId) ?? null,
    [editingMaterialId, intakeMaterials]
  );
  const shouldPollForMaterialAnalysis = useMemo(
    () =>
      intakeMaterials.some((material) => {
        const analysisStatus = material.analysisStatus as IntakeAnalysisStatus | null;
        return (
          (!material.summary && analysisStatus !== "FAILED" && analysisStatus !== "UNSUPPORTED") ||
          analysisStatus === "QUEUED" ||
          analysisStatus === "PROCESSING"
        );
      }),
    [intakeMaterials]
  );
  useEffect(() => {
    if (!intake) return;
    const formValues = toIntakeFormValues(intake);
    hydratedDraftTextKeyRef.current = getDraftTextKey(formValues);
    reset(formValues);
    setSaveState("saved");
  }, [intake, reset]);

  useEffect(() => {
    if (!shouldPollForMaterialAnalysis) {
      return undefined;
    }

    void refetch();

    const intervalId = setInterval(() => {
      void refetch();
    }, 1000);

    return () => clearInterval(intervalId);
  }, [refetch, shouldPollForMaterialAnalysis]);

  useEffect(() => {
    if (!intake?.id) return;
    if (autoExtractAttemptedIntakeIdsRef.current.has(intake.id)) return;
    if (outcomes.length > 0) return;
    if (isOutcomeApproved) return;
    if (isOutcomeSourceLoading) return;

    autoExtractAttemptedIntakeIdsRef.current.add(intake.id);

    const run = async () => {
      try {
        setSourceActionState({
          kind: "ai",
          status: "loading",
          message: "Looking for existing learning outcomes in your course content...",
        });
        const result = await startLearningOutcomeCourseContentExtraction({
          variables: { input: { modernizerIntakeId: intake.id } },
        });
        const payload = result.data?.startLearningOutcomeCourseContentExtraction;
        if (!payload?.success || !payload.jobId) {
          throw new Error(payload?.message ?? "Failed to start learning outcome extraction");
        }
        setLearningOutcomeExtractionJobId(payload.jobId);
      } catch {
        setSourceActionState({
          kind: "ai",
          status: "error",
          message: "Failed to start learning outcome extraction from course content.",
        });
      }
    };

    void run();
  }, [
    intake?.id,
    isOutcomeApproved,
    isOutcomeSourceLoading,
    outcomes.length,
    startLearningOutcomeCourseContentExtraction,
  ]);

  useEffect(() => {
    if (!learningOutcomeExtractionJobId) return;
    const timeoutId = setTimeout(() => {
      setLearningOutcomeExtractionJobId(null);
      setSourceActionState({
        kind: "ai",
        status: "error",
        message: "Learning outcome extraction is taking longer than expected. Please try again.",
      });
    }, LEARNING_OUTCOME_EXTRACTION_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [learningOutcomeExtractionJobId]);

  useEffect(() => {
    if (!learningOutcomeExtractionJobId) return;
    if (learningOutcomeExtractionStatusError) {
      setLearningOutcomeExtractionJobId(null);
      setSourceActionState({
        kind: "ai",
        status: "error",
        message: "Failed to check learning outcome extraction status.",
      });
    }
  }, [learningOutcomeExtractionJobId, learningOutcomeExtractionStatusError]);

  useEffect(() => {
    if (!learningOutcomeExtractionJobId) return;
    const payload = learningOutcomeExtractionStatusData?.getLearningOutcomeCourseContentExtractionStatus;
    if (!payload) return;
    if (payload.status === LearningOutcomeExtractionJobStatus.Completed) {
      setLearningOutcomeExtractionJobId(null);
      void refetch();
      setSourceActionState(
        (payload.count ?? 0) > 0
          ? null
          : {
              kind: "ai",
              status: "empty",
              message: "No existing learning outcomes were found in the linked course content.",
            }
      );
      return;
    }
    if (payload.status === LearningOutcomeExtractionJobStatus.Failed) {
      setLearningOutcomeExtractionJobId(null);
      setSourceActionState({
        kind: "ai",
        status: "error",
        message: payload.message ?? "Learning outcome extraction failed.",
      });
    }
  }, [learningOutcomeExtractionJobId, learningOutcomeExtractionStatusData, refetch]);

  const saveIntakeDraft = useCallback(
    async (values?: IntakeFormValues, options?: { includeMaterials?: boolean; throwOnError?: boolean }) => {
      if (!intake?.id) return;
      const draft = values ?? getValues();
      setSaveState("saving");
      try {
        const input = {
          intakeId: intake.id,
          visionIntent: draft.visionIntent,
          audienceNotes: draft.audienceNotes,
          signatureStyleTags: draft.signatureStyleTags,
          signatureStyleOther: draft.signatureStyleOther,
          ...(options?.includeMaterials ? { intakeMaterials: toDraftIntakeMaterials(draft.intakeMaterials) } : {}),
        };
        const result = await saveCourseModernizerIntakeDraft({
          variables: {
            input,
          },
        });
        const payload = result.data?.saveCourseModernizerIntakeDraft;
        if (!payload?.success) throw new Error(payload?.message ?? "Failed to save intake settings");
        hydratedDraftTextKeyRef.current = getDraftTextKey(draft);
        setSaveState("saved");
      } catch (saveError) {
        setSaveState("error");
        if (!options?.throwOnError) {
          toast?.showToast({ message: getErrorMessage(saveError), type: "error" });
        }
        if (options?.throwOnError) {
          throw saveError;
        }
      }
    },
    [getValues, intake?.id, saveCourseModernizerIntakeDraft, toast]
  );

  useEffect(() => {
    if (!intake?.id) return;
    if (
      debouncedAudienceNotes !== audienceNotes ||
      debouncedSignatureStyleOther !== signatureStyleOther ||
      debouncedVisionIntent !== visionIntent
    ) {
      return;
    }
    const draftTextKey = getDraftTextKey({
      audienceNotes: debouncedAudienceNotes,
      signatureStyleOther: debouncedSignatureStyleOther,
      visionIntent: debouncedVisionIntent,
    });
    if (draftTextKey === hydratedDraftTextKeyRef.current) {
      return;
    }
    void saveIntakeDraft({
      ...getValues(),
      audienceNotes: debouncedAudienceNotes,
      signatureStyleOther: debouncedSignatureStyleOther,
      visionIntent: debouncedVisionIntent,
    });
  }, [
    intake?.id,
    audienceNotes,
    debouncedAudienceNotes,
    debouncedSignatureStyleOther,
    debouncedVisionIntent,
    getValues,
    saveIntakeDraft,
    signatureStyleOther,
    visionIntent,
  ]);

  const handleAddManualOutcome = useCallback(
    async (text: string) => {
      if (!intake?.id) return;
      const nextText = text.trim();
      const result = await createLearningOutcome({
        variables: {
          input: {
            modernizerIntakeId: intake.id,
            text: nextText,
            position: outcomes.length + 1,
            isAIDrafted: false,
            sourceKind: LearningOutcomeSourceKind.Manual,
          },
        },
      });
      const payload = result.data?.createLearningOutcome;
      if (!payload?.success) {
        toast?.showToast({ message: payload?.message ?? "Failed to add outcome", type: "error" });
        throw new Error(payload?.message ?? "Failed to add outcome");
      }
      await refetch();
    },
    [createLearningOutcome, intake?.id, outcomes.length, refetch, toast]
  );

  const handleSaveOutcome = useCallback(
    async (outcome: IntakeOutcome, text: string) => {
      const result = await updateLearningOutcome({
        variables: {
          input: {
            id: outcome.id,
            text,
            isAIDrafted: false,
            originalText: outcome.text,
            sourceKind: LearningOutcomeSourceKind.Manual,
            sourceProvenance: getManualEditSourceProvenance(outcome),
          },
        },
      });
      const payload = result.data?.updateLearningOutcome;
      if (!payload?.success) {
        toast?.showToast({ message: payload?.message ?? "Failed to update outcome", type: "error" });
        return;
      }
      await refetch();
    },
    [refetch, toast, updateLearningOutcome]
  );

  const handleRemoveOutcome = useCallback(
    async (outcome: IntakeOutcome) => {
      const result = await deleteLearningOutcome({ variables: { id: outcome.id } });
      const payload = result.data?.deleteLearningOutcome;
      if (!payload?.success) {
        toast?.showToast({ message: payload?.message ?? "Failed to remove outcome", type: "error" });
        return;
      }
      await refetch();
    },
    [deleteLearningOutcome, refetch, toast]
  );

  const handleApproveOutcomes = useCallback(
    async (approveEmpty: boolean) => {
      if (!intake?.id) return;
      const result = await approveLearningOutcomeSet({
        variables: { input: { modernizerIntakeId: intake.id, approveEmpty } },
      });
      const payload = result.data?.approveLearningOutcomeSet;
      if (!payload?.success) {
        toast?.showToast({ message: payload?.message ?? "Failed to approve outcomes", type: "error" });
        return;
      }
      toast?.showToast({ message: "Learning outcomes approved", type: "success" });
      await refetch();
    },
    [approveLearningOutcomeSet, intake?.id, refetch, toast]
  );

  const closeAiGenerationWarningModal = useCallback(() => {
    setPendingAiGeneration(null);
  }, []);

  const closeUploadOutcomeWarningModal = useCallback(() => {
    setIsUploadOutcomeWarningOpen(false);
  }, []);

  const generateOutcomesWithAI = useCallback(
    async (instruction: string, targetCount?: number) => {
      if (!intake?.id) return;
      try {
        setSourceActionState({
          kind: "ai",
          status: "loading",
          message: "Generating learning outcomes from the available course context...",
        });
        const result = await generateLearningOutcomeSet({
          variables: {
            input: {
              modernizerIntakeId: intake.id,
              instruction: instruction.trim() || null,
              ...(targetCount ? { targetCount } : {}),
            },
          },
        });
        const payload = result.data?.generateLearningOutcomeSet;
        if (!payload?.success) throw new Error(payload?.message ?? "Failed to generate learning outcomes");
        toast?.showToast({ message: "AI generated learning outcomes", type: "success" });
        setSourceActionState(null);
        await refetch();
      } catch (generateError) {
        toast?.showToast({ message: getErrorMessage(generateError), type: "error" });
        setSourceActionState({
          kind: "ai",
          status: "error",
          message: "AI generation failed. Your current outcome set was not changed.",
        });
      }
    },
    [generateLearningOutcomeSet, intake?.id, refetch, toast]
  );

  const handleGenerateOutcomesWithAI = useCallback(
    (instruction: string, targetCount?: number) => {
      if (!intake?.id) return;
      const nextInstruction = instruction.trim();
      if (outcomes.length > 0) {
        setPendingAiGeneration({ instruction: nextInstruction, targetCount });
        return;
      }
      generateOutcomesWithAI(nextInstruction, targetCount);
    },
    [generateOutcomesWithAI, intake?.id, outcomes]
  );

  const confirmGenerateOutcomesWithAI = useCallback(async () => {
    const pendingGeneration = pendingAiGeneration;
    setPendingAiGeneration(null);
    await generateOutcomesWithAI(pendingGeneration?.instruction ?? "", pendingGeneration?.targetCount);
  }, [generateOutcomesWithAI, pendingAiGeneration]);

  const handleReviseOutcomeWithAI = useCallback(
    async (outcome: IntakeOutcome, instruction: string, selectedOptions: string[]) => {
      const result = await reviseLearningOutcomeWithAI({
        variables: {
          input: {
            learningOutcomeId: outcome.id,
            instruction: instruction.trim() || null,
            selectedOptions,
          },
        },
      });
      const payload = result.data?.reviseLearningOutcomeWithAI;
      if (!payload?.success) {
        toast?.showToast({ message: payload?.message ?? "Failed to revise outcome", type: "error" });
        return;
      }
      toast?.showToast({ message: "AI revised the learning outcome", type: "success" });
      await refetch();
    },
    [refetch, reviseLearningOutcomeWithAI, toast]
  );

  const handleRevertOutcomeAIRevision = useCallback(
    async (outcome: IntakeOutcome) => {
      const result = await revertLearningOutcomeAIRevision({
        variables: { input: { learningOutcomeId: outcome.id } },
      });
      const payload = result.data?.revertLearningOutcomeAIRevision;
      if (!payload?.success) {
        toast?.showToast({ message: payload?.message ?? "Failed to revert outcome", type: "error" });
        return;
      }
      toast?.showToast({ message: "Outcome reverted", type: "success" });
      await refetch();
    },
    [refetch, revertLearningOutcomeAIRevision, toast]
  );

  const toggleStyleTag = async (tag: string) => {
    const nextTags = signatureStyleTags.includes(tag)
      ? signatureStyleTags.filter((item) => item !== tag)
      : [...signatureStyleTags, tag];
    setValue("signatureStyleTags", nextTags, { shouldDirty: true });
    await saveIntakeDraft({ ...getValues(), signatureStyleTags: nextTags });
  };

  const persistIntakeMaterials = useCallback(
    async (materials: IntakeMaterial[], options?: { throwOnError?: boolean }) => {
      setValue("intakeMaterials", materials, { shouldDirty: true });
      await saveIntakeDraft(
        { ...getValues(), intakeMaterials: materials },
        { includeMaterials: true, throwOnError: options?.throwOnError }
      );
    },
    [getValues, saveIntakeDraft, setValue]
  );

  const appendMaterials = useCallback(
    async (pickedMaterials: IntakeMaterial[]) => {
      if (!pickedMaterials.length) return;
      const existingFileIds = new Set(intakeMaterials.map((material) => material.fileId));
      const newMaterials = pickedMaterials.filter((material) => !existingFileIds.has(material.fileId));
      if (!newMaterials.length) {
        return;
      }
      const nextMaterials: IntakeMaterial[] = [...intakeMaterials, ...newMaterials];
      await persistIntakeMaterials(nextMaterials);
    },
    [intakeMaterials, persistIntakeMaterials]
  );

  const clearOutcomeUploadTracking = useCallback(() => {
    currentOutcomeUploadFileIdsRef.current.forEach((fileId) => {
      const pickerKey = pickerMaterialKeysRef.current.get(fileId);
      if (pickerKey) {
        prevPickedKeysRef.current.delete(pickerKey);
        pickerMaterialKeysRef.current.delete(fileId);
      }
    });
    currentOutcomeUploadFileIdsRef.current.clear();
    lastUploadExtractionSignatureRef.current = null;
  }, []);

  const openOutcomeSourcePicker = useCallback(() => {
    clearOutcomeUploadTracking();
    setSourceActionState(null);

    if (Platform.OS === "web") {
      outcomeSourceFileInputRef.current?.click();
    }
  }, [clearOutcomeUploadTracking]);

  const handleMaterialsSelected = useCallback(
    async (items: PickedItem[], options?: { appendOnly?: boolean; forceDocumentFilter?: boolean }) => {
      const isUploadOutcomeFlow = options?.forceDocumentFilter === true;
      if (!isUploadOutcomeFlow && sourceActionState?.kind === "upload") {
        clearOutcomeUploadTracking();
        setSourceActionState(null);
      }
      const eligibleItems = isUploadOutcomeFlow ? items.filter(isDocumentPickerItem) : items;
      const rejectedCount = items.length - eligibleItems.length;
      if (rejectedCount > 0) {
        toast?.showToast({
          message: `Only allowed source material types are supported ${INTAKE_MATERIALS_FORMAT_HINT}.`,
          type: "error",
        });
      }
      if (eligibleItems.length === 0 && items.length > 0) {
        return;
      }
      if (isUploadOutcomeFlow && eligibleItems.length > 0) {
        eligibleItems.forEach((item) => {
          if (item.kind === "local" && item.uploadedFileId) {
            currentOutcomeUploadFileIdsRef.current.add(item.uploadedFileId);
          }
        });
        setSourceActionState({
          kind: "upload",
          status: "loading",
          message: "Uploading source document(s). Extraction will run automatically.",
        });
      }

      const previousKeys = isUploadOutcomeFlow ? new Set<string>() : prevPickedKeysRef.current;
      const eligibleKeys = eligibleItems.map(getPickedItemKey);
      const nextKeys = isUploadOutcomeFlow
        ? previousKeys
        : options?.appendOnly
          ? new Set([...previousKeys, ...eligibleKeys])
          : new Set(eligibleKeys);
      const removedKeys =
        isUploadOutcomeFlow || options?.appendOnly ? [] : [...previousKeys].filter((key) => !nextKeys.has(key));
      const removedKeySet = new Set(removedKeys);
      const newItems = isUploadOutcomeFlow
        ? eligibleItems
        : eligibleItems.filter((item) => !previousKeys.has(getPickedItemKey(item)));
      if (!isUploadOutcomeFlow) {
        prevPickedKeysRef.current = nextKeys;
      }

      let nextMaterials = intakeMaterials;
      if (removedKeySet.size > 0) {
        setPendingExternalMaterials((current) => current.filter((material) => !removedKeySet.has(material.pickerKey)));
        setExternalJobIds((current) =>
          current.filter((jobId) => {
            const pickerKey = externalJobPickerKeysRef.current.get(jobId);
            if (pickerKey && removedKeySet.has(pickerKey)) {
              externalJobPickerKeysRef.current.delete(jobId);
              externalJobConnectorTypesRef.current.delete(jobId);
              return false;
            }
            return !pickerKey || !removedKeySet.has(pickerKey);
          })
        );
        nextMaterials = intakeMaterials.filter((material) => {
          const pickerKey = pickerMaterialKeysRef.current.get(material.fileId);
          return !pickerKey || !removedKeySet.has(pickerKey);
        });
        intakeMaterials.forEach((material) => {
          const pickerKey = pickerMaterialKeysRef.current.get(material.fileId);
          if (pickerKey && removedKeySet.has(pickerKey)) {
            pickerMaterialKeysRef.current.delete(material.fileId);
          }
        });
      }

      const localMaterials: IntakeMaterial[] = newItems.flatMap((item, i) => {
        if (item.kind !== "local" || !item.uploadedFileId) return [];
        if (!isUploadOutcomeFlow) {
          pickerMaterialKeysRef.current.set(item.uploadedFileId, getPickedItemKey(item));
        }
        return [
          {
            id: `pending-${item.uploadedFileId}-${Date.now()}-${i}`,
            fileId: item.uploadedFileId,
            position: null,
            title: item.name,
            fileUrl: item.url ?? null,
            fileStatus: null,
            retentionPolicy: CourseIntakeMaterialRetentionPolicy.Retain,
          },
        ];
      });
      const localItemsMissingFile = newItems.some((item) => item.kind === "local" && !item.uploadedFileId);
      if (localItemsMissingFile) {
        toast?.showToast({ message: "Upload materials before adding them to the intake.", type: "error" });
      }
      if (removedKeySet.size > 0 || localMaterials.length > 0) {
        const existingFileIds = new Set(nextMaterials.map((material) => material.fileId));
        const uniqueLocalMaterials = localMaterials.filter((material) => !existingFileIds.has(material.fileId));
        await persistIntakeMaterials([...nextMaterials, ...uniqueLocalMaterials]);
      }

      if (!newItems.length) return;

      const externalItems = newItems.filter((item): item is ExternalPickedItem => item.kind === "external");
      if (!externalItems.length) return;

      try {
        const { grouped, rejected } = convertExternalPickedItemsToAttachmentInput(externalItems);
        if (rejected.length > 0) {
          toast?.showToast({
            message: `Some materials could not be imported: ${rejected.map(({ item }) => item.name).join(", ")}`,
            type: "error",
          });
        }

        const queuedJobIds: string[] = [];
        for (const group of grouped) {
          const result = await attachExternal({
            connectorType: group.connectorType,
            externalItemIds: group.externalItemIds,
            strategy: ExternalSourceChunkingStrategyInput.RecursiveTextSplitting,
          });
          const itemJobPairs = pairExternalItemsWithQueuedJobs(
            group.items,
            result?.attachExternalContent?.data?.itemResults ?? []
          );
          itemJobPairs.forEach(({ item, jobId }) => {
            externalJobPickerKeysRef.current.set(jobId, getPickedItemKey(item));
            externalJobConnectorTypesRef.current.set(jobId, item.connectorType);
          });
          queuedJobIds.push(...itemJobPairs.map(({ jobId }) => jobId));
          setPendingExternalMaterials((current) => [
            ...current,
            ...itemJobPairs.map(({ item, jobId }, index) => ({
              id: `external-${item.externalId}-${jobId}-${index}`,
              name: item.name,
              externalJobId: jobId,
              pickerKey: getPickedItemKey(item),
              status: "pending" as const,
            })),
          ]);
        }
        if (queuedJobIds.length > 0) {
          setExternalJobIds((current) => [...current, ...queuedJobIds]);
        }
      } catch (pickerError) {
        toast?.showToast({ message: getErrorMessage(pickerError), type: "error" });
      }
    },
    [attachExternal, clearOutcomeUploadTracking, intakeMaterials, persistIntakeMaterials, sourceActionState, toast]
  );

  const handleUploadPdfOrDoc = useCallback(() => {
    pendingDroppedFilesRef.current = null;
    if (outcomes.length > 0) {
      setIsUploadOutcomeWarningOpen(true);
      return;
    }
    openOutcomeSourcePicker();
  }, [openOutcomeSourcePicker, outcomes]);

  const processDroppedFiles = useCallback(
    async (files: File[]) => {
      const supportedFilesWithMimeType = files.flatMap((file) => {
        const mimeType = getAllowedDocumentFileMimeType(file);
        return mimeType ? [{ file, mimeType }] : [];
      });
      const rejectedCount = files.length - supportedFilesWithMimeType.length;
      if (rejectedCount > 0) {
        toast?.showToast({
          message: "Only PDF, DOC, or DOCX files are supported for outcome extraction.",
          type: "error",
        });
      }

      if (!supportedFilesWithMimeType.length) {
        setSourceActionState({
          kind: "upload",
          status: "empty",
          message: "Choose a PDF, DOC, or DOCX file to extract candidate outcomes.",
        });
        return;
      }

      clearOutcomeUploadTracking();
      setSourceActionState({
        kind: "upload",
        status: "loading",
        message: "Uploading source document(s). Extraction will run automatically.",
      });

      const pickedItems: PickedItem[] = [];
      try {
        for (const { file, mimeType } of supportedFilesWithMimeType) {
          const uri = await readFileAsDataUrl(file);
          const result = await uploadOutcomeSourceFile({
            uri,
            mimeType,
            size: file.size,
            name: file.name,
          });

          if (result?.file?.id) {
            pickedItems.push({
              kind: "local",
              file,
              name: file.name,
              mimeType,
              sizeBytes: file.size,
              uploadedFileId: result.file.id,
              url: result.file.fileURL,
            });
          }
        }
      } catch (uploadError) {
        setSourceActionState({
          kind: "upload",
          status: "error",
          message: getErrorMessage(uploadError),
        });
        return;
      }

      if (!pickedItems.length) {
        setSourceActionState({
          kind: "upload",
          status: "error",
          message: "Failed to upload source document.",
        });
        return;
      }

      await handleMaterialsSelected(pickedItems, { appendOnly: true, forceDocumentFilter: true });
    },
    [clearOutcomeUploadTracking, handleMaterialsSelected, toast, uploadOutcomeSourceFile]
  );

  const confirmUploadPdfOrDoc = useCallback(() => {
    setIsUploadOutcomeWarningOpen(false);
    const droppedFiles = pendingDroppedFilesRef.current;
    pendingDroppedFilesRef.current = null;
    if (droppedFiles?.length) {
      void processDroppedFiles(droppedFiles);
    } else {
      openOutcomeSourcePicker();
    }
  }, [openOutcomeSourcePicker, processDroppedFiles]);

  const handleOutcomeSourceFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      event.target.value = "";
      if (!files.length) return;

      const supportedFilesWithMimeType = files.flatMap((file) => {
        const mimeType = getAllowedDocumentFileMimeType(file);
        return mimeType ? [{ file, mimeType }] : [];
      });
      const rejectedCount = files.length - supportedFilesWithMimeType.length;
      if (rejectedCount > 0) {
        toast?.showToast({
          message: "Only PDF, DOC, or DOCX files are supported for outcome extraction.",
          type: "error",
        });
      }

      if (!supportedFilesWithMimeType.length) {
        setSourceActionState({
          kind: "upload",
          status: "empty",
          message: "Choose a PDF, DOC, or DOCX file to extract candidate outcomes.",
        });
        return;
      }

      setSourceActionState({
        kind: "upload",
        status: "loading",
        message: "Uploading source document(s). Extraction will run automatically.",
      });

      const pickedItems: PickedItem[] = [];
      try {
        for (const { file, mimeType } of supportedFilesWithMimeType) {
          const uri = await readFileAsDataUrl(file);
          const result = await uploadOutcomeSourceFile({
            uri,
            mimeType,
            size: file.size,
            name: file.name,
          });

          if (result?.file?.id) {
            pickedItems.push({
              kind: "local",
              file,
              name: file.name,
              mimeType,
              sizeBytes: file.size,
              uploadedFileId: result.file.id,
              url: result.file.fileURL,
            });
          }
        }
      } catch (uploadError) {
        setSourceActionState({
          kind: "upload",
          status: "error",
          message: getErrorMessage(uploadError),
        });
        return;
      }

      if (!pickedItems.length) {
        setSourceActionState({
          kind: "upload",
          status: "error",
          message: "Failed to upload source document.",
        });
        return;
      }

      await handleMaterialsSelected(pickedItems, { appendOnly: true, forceDocumentFilter: true });
    },
    [handleMaterialsSelected, toast, uploadOutcomeSourceFile]
  );

  const handleDroppedFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return;

      if (outcomes.length > 0) {
        pendingDroppedFilesRef.current = files;
        setIsUploadOutcomeWarningOpen(true);
        return;
      }

      await processDroppedFiles(files);
    },
    [outcomes, processDroppedFiles]
  );

  const updateMaterial = async (
    materialId: string,
    patch: Partial<IntakeMaterial>,
    options?: { throwOnError?: boolean }
  ) => {
    const nextMaterials = intakeMaterials.map((material) =>
      material.id === materialId ? { ...material, ...patch } : material
    );
    await persistIntakeMaterials(nextMaterials, options);
  };

  const removeMaterial = async (materialId: string) => {
    const nextMaterials = intakeMaterials.filter((material) => material.id !== materialId);
    await persistIntakeMaterials(nextMaterials);
  };

  const retryMaterialSummary = useCallback(
    async (materialId: string) => {
      if (!intake?.id) return;
      setRetryingMaterialId(materialId);
      try {
        const result = await saveCourseModernizerIntakeDraft({
          variables: {
            input: {
              intakeId: intake.id,
              retryMaterialId: materialId,
            },
          },
        });
        if (!result.data?.saveCourseModernizerIntakeDraft?.success) {
          throw new Error(result.data?.saveCourseModernizerIntakeDraft?.message ?? "Failed to queue summary retry");
        }
        toast?.showToast({
          message: "Summary retry queued. Summary is being processed, please be patient.",
          type: "success",
        });
        await refetch();
      } catch (retryError) {
        toast?.showToast({ message: getErrorMessage(retryError), type: "error" });
      } finally {
        setRetryingMaterialId(null);
      }
    },
    [intake?.id, refetch, saveCourseModernizerIntakeDraft, toast]
  );

  useEffect(() => {
    if (jobStatuses.size === 0) return;
    jobStatuses.forEach((jobStatus, jobId) => {
      if (!jobStatus.isTerminal || processedExternalJobsRef.current.has(jobId)) {
        if (jobStatus.category === "processing") {
          setPendingExternalMaterials((current) =>
            current.map((material) =>
              material.externalJobId === jobId ? { ...material, status: "processing" as const } : material
            )
          );
        }
        return;
      }
      processedExternalJobsRef.current.add(jobId);
      if (jobStatus.category === "completed" && jobStatus.file?.id) {
        const pickerKey = externalJobPickerKeysRef.current.get(jobId);
        if (pickerKey && !prevPickedKeysRef.current.has(pickerKey)) {
          externalJobPickerKeysRef.current.delete(jobId);
          externalJobConnectorTypesRef.current.delete(jobId);
          return;
        }
        if (pickerKey) {
          pickerMaterialKeysRef.current.set(jobStatus.file.id, pickerKey);
        }
        const newMaterial: IntakeMaterial = {
          id: `pending-${jobStatus.file.id}-${Date.now()}`,
          fileId: jobStatus.file.id,
          position: null,
          title: jobStatus.file.fileName ?? "Imported material",
          fileUrl: jobStatus.file.fileURL ?? null,
          fileStatus: jobStatus.file.status ?? null,
          sourceConnectorType: externalJobConnectorTypesRef.current.get(jobId) ?? null,
          retentionPolicy: CourseIntakeMaterialRetentionPolicy.Retain,
        };
        void appendMaterials([newMaterial]).then(() => {
          setPendingExternalMaterials((current) => current.filter((material) => material.externalJobId !== jobId));
          void refetch();
        });
        return;
      }
      if (jobStatus.category === "failed") {
        setPendingExternalMaterials((current) =>
          current.map((material) =>
            material.externalJobId === jobId
              ? {
                  ...material,
                  status: "failed" as const,
                  message: jobStatus.failedReason ?? "Import failed",
                }
              : material
          )
        );
      }
    });
  }, [appendMaterials, jobStatuses]);

  useEffect(() => {
    if (!intake?.id) return;
    if (sourceActionState?.kind !== "upload") return;
    if (isGeneratingUploadOutcomesRef.current) return;

    const currentOutcomeUploadFileIds = currentOutcomeUploadFileIdsRef.current;
    const uploadMaterials =
      currentOutcomeUploadFileIds.size === 0
        ? []
        : intakeMaterials.filter((material) => currentOutcomeUploadFileIds.has(material.fileId));

    if (uploadMaterials.length === 0) {
      if (currentOutcomeUploadFileIds.size > 0) {
        const attachingMessage = "Uploading source document(s). Extraction will run automatically.";
        if (sourceActionState.status !== "loading" || sourceActionState.message !== attachingMessage) {
          setSourceActionState({
            kind: "upload",
            status: "loading",
            message: attachingMessage,
          });
        }
        return;
      }

      return;
    }

    const hasPendingAnalysis = uploadMaterials.some((material) => {
      const status = material.analysisStatus as IntakeAnalysisStatus | null;
      return (
        status === "QUEUED" ||
        status === "PROCESSING" ||
        (!material.summary && status !== "FAILED" && status !== "UNSUPPORTED")
      );
    });

    if (hasPendingAnalysis) {
      const processingMessage =
        "Processing uploaded document(s). Extracted outcomes will appear when parsing completes.";
      if (sourceActionState.message !== processingMessage) {
        setSourceActionState({
          kind: "upload",
          status: "loading",
          message: processingMessage,
        });
      }
      return;
    }

    const allFailed = uploadMaterials.every((material) => {
      const status = material.analysisStatus as IntakeAnalysisStatus | null;
      return status === "FAILED" || status === "UNSUPPORTED";
    });
    if (allFailed) {
      clearOutcomeUploadTracking();
      setSourceActionState({
        kind: "upload",
        status: "error",
        message: "Document analysis failed. Try uploading a different file or retry from the materials list.",
      });
      return;
    }

    const extractionSignature = uploadMaterials
      .map((material) => `${material.fileId}:${material.analysisStatus ?? ""}:${material.summary ?? ""}`)
      .join("|");
    if (lastUploadExtractionSignatureRef.current === extractionSignature) {
      return;
    }

    const run = async () => {
      try {
        isGeneratingUploadOutcomesRef.current = true;
        setSourceActionState({
          kind: "upload",
          status: "loading",
          message: "Generating learning outcomes from uploaded source document(s)...",
        });
        const result = await generateLearningOutcomeCandidatesFromIntakeSignals({
          variables: { input: { modernizerIntakeId: intake.id } },
        });
        const payload = result.data?.generateLearningOutcomeCandidatesFromIntakeSignals;
        if (!payload?.success) {
          throw new Error(payload?.message ?? "Failed to generate extracted outcomes");
        }

        lastUploadExtractionSignatureRef.current = extractionSignature;
        await refetch();
        const createdCount = payload.count ?? 0;
        clearOutcomeUploadTracking();
        setSourceActionState(
          createdCount > 0
            ? null
            : {
                kind: "upload",
                status: "empty",
                message: "No new outcomes found in the selected source material.",
              }
        );
      } catch (uploadExtractError) {
        clearOutcomeUploadTracking();
        setSourceActionState({
          kind: "upload",
          status: "error",
          message: getErrorMessage(uploadExtractError),
        });
      } finally {
        isGeneratingUploadOutcomesRef.current = false;
      }
    };

    void run();
  }, [
    clearOutcomeUploadTracking,
    generateLearningOutcomeCandidatesFromIntakeSignals,
    intake?.id,
    intakeMaterials,
    refetch,
    sourceActionState,
  ]);

  const openInstructionsModal = (material: IntakeMaterial) => {
    setEditingMaterialId(material.id);
    setInstructionsDraft(material.instructions ?? "");
  };

  const closeInstructionsModal = () => {
    setEditingMaterialId(null);
    setInstructionsDraft("");
    setIsInstructionsFocused(false);
  };

  const saveInstructions = async () => {
    if (!editingMaterial) return;
    try {
      await updateMaterial(editingMaterial.id, { instructions: instructionsDraft }, { throwOnError: true });
      closeInstructionsModal();
    } catch (saveError) {
      toast?.showToast({ message: getErrorMessage(saveError), type: "error" });
    }
  };

  const handleModernize = handleSubmit(async (values) => {
    if (isModernizeDisabled || !intake?.id) return;
    try {
      await saveIntakeDraft(values, { includeMaterials: true, throwOnError: true });
      const result = await submitCourseModernizerIntake({
        variables: {
          input: {
            externalCourseId,
            modernizerIntakeId: intake.id,
          },
        },
      });
      const payload = result.data?.submitCourseModernizerIntake;
      if (!payload?.success || !payload.jobId) {
        throw new Error(payload?.message ?? "Failed to start course modernization");
      }
      toast?.showToast({ message: "Course modernization started", type: "success" });
      navigation.navigate("Course Modernizer", {
        activeExternalCourseId: externalCourseId,
        activeModernizeJobId: payload.jobId,
      });
    } catch (modernizeError) {
      toast?.showToast({ message: getErrorMessage(modernizeError), type: "error" });
    }
  });

  return (
    <View style={styles.page}>
      {Platform.OS === "web" ? (
        <input
          ref={outcomeSourceFileInputRef}
          type="file"
          accept={DOCUMENT_PICKER_ACCEPT}
          multiple
          onChange={(event) => void handleOutcomeSourceFileChange(event)}
          aria-label="Upload PDF or DOC source material"
          style={{ display: "none" }}
        />
      ) : null}
      <View style={styles.shell}>
        <View style={styles.topBar}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Exit intake wizard"
            onPress={() => navigation.navigate("Course Modernizer")}
            style={styles.exitButton}>
            <ArrowLeftIcon color="#334155" size={16} weight="bold" />
            <CustomText text="Exit" size="s" useTranslationText={false} style={styles.exitText} />
          </Pressable>
          <CustomText
            text={`Modernize ${intake?.name ?? "course"}`}
            size="s"
            weight="bold"
            useTranslationText={false}
            style={styles.topBarTitle}
          />
        </View>

        <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
          <View style={styles.hero}>
            <CustomText
              text="MODERNIZE A COURSE"
              size="xs"
              weight="bold"
              useTranslationText={false}
              style={styles.eyebrow}
            />
            <CustomText
              text={`Let's modernize ${intake?.name ?? "this course"}`}
              size="s"
              textType="display"
              weight="bold"
              role="heading"
              aria-level="1"
              useTranslationText={false}
              style={styles.title}
            />
            <CustomText
              text={
                intake
                  ? [intake.courseCode, intake.term, intake.lengthInWeeks ? `${intake.lengthInWeeks} weeks` : null]
                      .filter(Boolean)
                      .join(" · ")
                  : "Loading course modernizer intake"
              }
              size="s"
              useTranslationText={false}
              style={styles.subtitle}
            />
          </View>

          {loading ? (
            <Panel>
              <CustomText text="Loading intake workspace..." size="s" useTranslationText={false} />
            </Panel>
          ) : error || !intake ? (
            <Panel>
              <CustomText
                text={error?.message ?? "Intake workspace is not available for this course."}
                size="s"
                useTranslationText={false}
                style={styles.errorText}
              />
              <Pressable accessibilityRole="button" onPress={() => void refetch()} style={styles.linkButton}>
                <CustomText text="Retry" size="s" weight="bold" useTranslationText={false} style={styles.blueText} />
              </Pressable>
            </Panel>
          ) : (
            <>
              <CourseModernizerLearningOutcomes
                outcomes={outcomes.map((outcome, index) => ({
                  id: outcome.id,
                  label: getOutcomeLabel(index + 1),
                  text: outcome.text,
                  aiBadgeLabel: getOutcomeBadgeLabel(outcome),
                  canRevertAIRevision: hasRevertableOutcomeText(outcome),
                }))}
                approved={isOutcomeApproved}
                busy={
                  isApproving ||
                  isCreatingOutcome ||
                  isGeneratingOutcomes ||
                  isRevisingOutcome ||
                  isRevertingOutcome ||
                  isOutcomeSourceLoading
                }
                sourceStatus={sourceActionState?.status ?? null}
                sourceMessage={sourceActionState?.message ?? null}
                onAddManual={handleAddManualOutcome}
                onUploadPdfOrDoc={handleUploadPdfOrDoc}
                onDropFiles={handleDroppedFiles}
                onGenerateWithAI={handleGenerateOutcomesWithAI}
                onApprove={(approveEmpty) => void handleApproveOutcomes(approveEmpty)}
                onSaveOutcome={(outcomeId, text) => {
                  const outcome = outcomes.find((item) => item.id === outcomeId);
                  if (!outcome) return;
                  return handleSaveOutcome(outcome, text);
                }}
                onDeleteOutcome={(outcomeId) => {
                  const outcome = outcomes.find((item) => item.id === outcomeId);
                  if (!outcome) return;
                  return handleRemoveOutcome(outcome);
                }}
                onReviseOutcome={(outcomeId, instruction, selectedOptions) => {
                  const outcome = outcomes.find((item) => item.id === outcomeId);
                  if (!outcome) return;
                  return handleReviseOutcomeWithAI(outcome, instruction, selectedOptions);
                }}
                onRevertOutcome={(outcomeId) => {
                  const outcome = outcomes.find((item) => item.id === outcomeId);
                  if (!outcome) return;
                  return handleRevertOutcomeAIRevision(outcome);
                }}
              />

              <View style={styles.accordionShell}>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => setIsAdvancedOpen((value) => !value)}
                  style={styles.accordionHeader}>
                  <View style={styles.advancedIcon}>
                    <SlidersHorizontalIcon color="#334155" size={18} />
                  </View>
                  <View style={styles.accordionTitle}>
                    <CustomText text="Advanced settings" size="m" weight="bold" useTranslationText={false} />
                    <CustomText
                      text="Vision, audience, signature style, and intake materials are optional."
                      size="s"
                      useTranslationText={false}
                      style={styles.panelHint}
                    />
                  </View>
                  {isAdvancedOpen ? (
                    <CaretUpIcon color="#334155" size={18} />
                  ) : (
                    <CaretDownIcon color="#334155" size={18} />
                  )}
                </Pressable>
              </View>

              {isAdvancedOpen ? (
                <View style={styles.advancedStack}>
                  <Panel>
                    <SectionTitle title="Source materials" optional />
                    <CustomText
                      text="Drop textbooks, supplemental docs, or industry standards the modernizer should reference. You can also add these later from Course Details."
                      size="s"
                      useTranslationText={false}
                      style={styles.panelHint}
                    />
                    <ContentPicker
                      onFilesSelected={handleMaterialsSelected}
                      multiSelect={true}
                      storagePath={StoragePathType.IntakeMaterials}
                      allowLinks={true}
                      uploadLinksAsFiles={true}
                      isSelectingList={false}
                      context={DEFAULT_CONTEXT}
                      allowedMimeTypes={Array.from(DOCUMENT_PICKER_MIME_TYPES)}
                      allowedFileExtensions={Array.from(DOCUMENT_PICKER_EXTENSIONS)}
                      uploadFormatsLabel={INTAKE_MATERIALS_FORMAT_HINT}
                    />
                    {pendingExternalMaterials.map((material) => (
                      <MaterialCardShell key={material.id}>
                        <View style={styles.materialCardContent}>
                          <View style={styles.materialIcon}>
                            <FileTextIcon color="#DC2626" size={18} />
                          </View>
                          <View style={styles.materialCardMain}>
                            <CustomText text={material.name} size="s" weight="bold" useTranslationText={false} />
                            <CustomText
                              text={
                                material.status === "failed"
                                  ? (material.message ?? "Import failed")
                                  : material.status === "processing"
                                    ? "Importing connector file..."
                                    : "Queued for import..."
                              }
                              size="xs"
                              useTranslationText={false}
                              style={material.status === "failed" ? styles.errorText : styles.panelHint}
                            />
                          </View>
                        </View>
                      </MaterialCardShell>
                    ))}
                    {intakeMaterials.map((material) => (
                      <MaterialCardShell key={material.id}>
                        <View style={styles.materialCardContent}>
                          <View style={styles.materialIcon}>
                            <FileTextIcon color="#DC2626" size={18} />
                          </View>
                          <View style={styles.materialCardMain}>
                            <View style={styles.materialTitleRow}>
                              <View style={styles.materialTitleBlock}>
                                <CustomText text={material.title} size="s" weight="bold" useTranslationText={false} />
                                <View style={styles.materialMetadataRow}>
                                  <CustomText
                                    text={getMaterialSourceLabel(material)}
                                    size="xs"
                                    useTranslationText={false}
                                    style={styles.panelHint}
                                  />
                                  <CustomText text="•" size="xs" useTranslationText={false} style={styles.panelHint} />
                                  <CustomText
                                    text={getMaterialSummaryLabel(material)}
                                    size="xs"
                                    weight="bold"
                                    useTranslationText={false}
                                    style={[
                                      styles.materialMetadataSummaryText,
                                      getMaterialSummaryUiStatus(material) === "generated"
                                        ? styles.summaryReadyText
                                        : getMaterialSummaryUiStatus(material) === "generating"
                                          ? styles.blueText
                                          : styles.errorText,
                                    ]}
                                  />
                                  {getMaterialSummaryUiStatus(material) === "generating" ? (
                                    <ActivityIndicator size="small" color="#1D4ED8" />
                                  ) : null}
                                  {canRetryMaterialSummary(material) ? (
                                    <Pressable
                                      accessibilityRole="button"
                                      accessibilityLabel={`Retry summary generation for ${material.title}`}
                                      accessibilityHint="Queues summary generation again for this material."
                                      accessibilityState={{ disabled: retryingMaterialId === material.id }}
                                      onPress={() => void retryMaterialSummary(material.id)}
                                      style={retryingMaterialId === material.id ? styles.disabledButton : undefined}
                                      disabled={retryingMaterialId === material.id}>
                                      {retryingMaterialId === material.id ? (
                                        <ActivityIndicator size="small" color="#1D4ED8" />
                                      ) : (
                                        <RotateCwIcon size={12} color="#DC2626" />
                                      )}
                                    </Pressable>
                                  ) : null}
                                </View>
                              </View>
                              <View style={styles.materialActions}>
                                <IconButton
                                  label={
                                    material.instructions?.trim()
                                      ? `Edit instructions for ${material.title}`
                                      : `Add instructions for ${material.title}`
                                  }
                                  tooltipLabel={
                                    material.instructions?.trim() ? "Edit instructions" : "Add instructions"
                                  }
                                  onPress={() => openInstructionsModal(material)}
                                  icon={
                                    <FilePenLineIcon
                                      size={16}
                                      color={material.instructions?.trim() ? "#1D4ED8" : "#94A3B8"}
                                      strokeWidth={material.instructions?.trim() ? 2.5 : 2}
                                    />
                                  }
                                  compact
                                />
                                <IconButton
                                  label={`Remove Material ${material.title}`}
                                  tooltipLabel="Remove Material"
                                  onPress={() => removeMaterial(material.id)}
                                  icon={<TrashIcon size={16} color="#94A3B8" />}
                                  compact
                                />
                              </View>
                            </View>
                            {material.instructions?.trim() ? (
                              <View style={styles.materialNoteRow}>
                                <FilePenLineIcon color="#7C3AED" size={15} />
                                <CustomText
                                  text={material.instructions}
                                  size="xs"
                                  useTranslationText={false}
                                  style={styles.materialNote}
                                />
                              </View>
                            ) : null}
                            <View style={styles.materialSummaryRow}>
                              <SparkleIcon color="#A855F7" size={15} />
                              <CustomText
                                text={getMaterialSummaryText(material)}
                                size="xs"
                                weight="bold"
                                useTranslationText={false}
                                style={[
                                  styles.materialSummaryText,
                                  material.analysisStatus === "FAILED" || material.analysisStatus === "UNSUPPORTED"
                                    ? styles.errorText
                                    : null,
                                ]}
                              />
                            </View>
                          </View>
                        </View>
                        <View style={styles.retentionRow}>
                          <CustomText
                            text="After modernization:"
                            size="xs"
                            useTranslationText={false}
                            style={styles.panelHint}
                          />
                          <RetentionButton
                            label="Retain"
                            selected={material.retentionPolicy === CourseIntakeMaterialRetentionPolicy.Retain}
                            onPress={() =>
                              void updateMaterial(material.id, {
                                retentionPolicy: CourseIntakeMaterialRetentionPolicy.Retain,
                              })
                            }
                          />
                          <RetentionButton
                            label="Purge"
                            selected={
                              material.retentionPolicy === CourseIntakeMaterialRetentionPolicy.PurgeAfterModernization
                            }
                            onPress={() =>
                              void updateMaterial(material.id, {
                                retentionPolicy: CourseIntakeMaterialRetentionPolicy.PurgeAfterModernization,
                              })
                            }
                          />
                        </View>
                      </MaterialCardShell>
                    ))}
                  </Panel>

                  <Controller
                    control={control}
                    name="visionIntent"
                    render={({ field: { onChange, value } }) => (
                      <GuidedTextPanel
                        title="Vision & intent"
                        helperText="What do you want this modernization to accomplish?"
                        accessibilityLabel="Vision and intent"
                        value={value}
                        onChangeText={onChange}
                        placeholder="Describe what you want this modernization to accomplish. The more specific you are, the better the recommendations — pull from the prompts above or write in your own words."
                        icon={<LightbulbIcon color="#1D4ED8" size={14} weight="fill" />}
                        prompts={[
                          "What frustrates you about this course?",
                          "What do students struggle with most?",
                          "If you could change one thing, what would it be?",
                          "What do you wish you had time to add?",
                          "Are you considering any strategic changes (textbooks, exams to projects, delivery mode)?",
                        ]}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="audienceNotes"
                    render={({ field: { onChange, value } }) => (
                      <GuidedTextPanel
                        title="Audience"
                        helperText="Your admin set the basic audience type — give us color about who's actually in this class so we can pitch examples, pacing, and tone."
                        accessibilityLabel="Audience notes"
                        value={value}
                        onChangeText={onChange}
                        placeholder="Tell us about who's actually in this class — beyond the basic category your admin set. The AI uses this to pitch examples, pacing, and tone."
                        icon={<SparkleIcon color="#1D4ED8" size={14} weight="bold" />}
                        prompts={[
                          "Adult learners returning to school?",
                          "High schoolers in a dual-enrollment program?",
                          "Working professionals taking this part-time?",
                          "Students with special-education accommodations?",
                          "Mixed cohort with very different prior experience?",
                        ]}
                      />
                    )}
                  />

                  <Panel>
                    <SectionTitle title="Signature style" optional />
                    <View style={styles.tagGrid}>
                      {STYLE_TAGS.map((tag) => (
                        <Pressable
                          key={tag}
                          accessibilityRole="button"
                          onPress={() => void toggleStyleTag(tag)}
                          style={[styles.tag, signatureStyleTags.includes(tag) && styles.tagSelected]}>
                          <CustomText
                            text={tag}
                            size="xs"
                            useTranslationText={false}
                            style={signatureStyleTags.includes(tag) ? styles.blueText : styles.tagText}
                          />
                        </Pressable>
                      ))}
                    </View>
                    <CustomText
                      text="ANYTHING NOT COVERED ABOVE?"
                      size="xs"
                      weight="bold"
                      useTranslationText={false}
                      style={styles.signatureOtherLabel}
                    />
                    <Controller
                      control={control}
                      name="signatureStyleOther"
                      render={({ field: { onChange, value } }) => (
                        <IntakeTextArea
                          accessibilityLabel="Custom signature style notes"
                          value={value}
                          onChangeText={onChange}
                          placeholder='e.g., "I use humor and current events", "I reference my industry experience frequently"'
                          compact
                        />
                      )}
                    />
                    <CustomText
                      text={saveState === "saving" ? "Saving..." : saveState === "error" ? "Save failed" : "Saved"}
                      size="xs"
                      useTranslationText={false}
                      style={saveState === "error" ? styles.errorText : styles.panelHint}
                    />
                  </Panel>
                </View>
              ) : null}
            </>
          )}
        </ScrollView>

        <View style={styles.stickyFooter}>
          <View style={styles.footerContent}>
            {isReadyToModernize ? (
              <CustomText
                text={footerReadinessMessage}
                size="xs"
                useTranslationText={false}
                style={styles.footerNote}
              />
            ) : (
              <CustomText
                text={footerReadinessMessage}
                size="xs"
                useTranslationText={false}
                style={[styles.footerNote, styles.errorText]}
              />
            )}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Submit intake for modernization"
              accessibilityState={{ disabled: isModernizeDisabled }}
              disabled={isModernizeDisabled}
              onPress={() => void handleModernize()}
              style={[styles.modernizeButton, isModernizeDisabled && styles.disabledButton]}>
              <SparkleIcon color="#FFFFFF" size={15} weight="bold" />
              <CustomText
                text={isSubmittingModernizerIntake ? "Modernizing..." : "Modernize"}
                size="s"
                weight="bold"
                useTranslationText={false}
                style={styles.primaryButtonText}
              />
              <ArrowRightIcon color="#FFFFFF" size={15} weight="bold" />
            </Pressable>
          </View>
        </View>
        <Modal
          animationType="fade"
          transparent
          visible={Boolean(editingMaterial)}
          onRequestClose={closeInstructionsModal}>
          <View style={styles.modalBackdrop}>
            <View style={styles.instructionsModal}>
              <View style={styles.modalHeader}>
                <View>
                  <CustomText
                    text="Instructions for this material"
                    size="m"
                    weight="bold"
                    useTranslationText={false}
                    style={styles.modalTitle}
                  />
                  <CustomText
                    text="Add any guidance for how Axio should use this source."
                    size="s"
                    useTranslationText={false}
                    style={styles.panelHint}
                  />
                </View>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Close instructions modal"
                  onPress={closeInstructionsModal}
                  style={styles.closeButton}>
                  <CustomText text="x" size="m" useTranslationText={false} style={styles.closeButtonText} />
                </Pressable>
              </View>
              <TextInput
                accessibilityLabel="Material modernizer instructions"
                multiline
                numberOfLines={6}
                value={instructionsDraft}
                onChangeText={setInstructionsDraft}
                onBlur={() => setIsInstructionsFocused(false)}
                onFocus={() => setIsInstructionsFocused(true)}
                placeholder="e.g., Use only pages 10-25, ignore the appendix..."
                placeholderTextColor={colors.grey[400]}
                style={[styles.instructionsTextArea, isInstructionsFocused && styles.instructionsTextAreaFocused]}
              />
              <View style={styles.modalFooter}>
                <Pressable accessibilityRole="button" onPress={closeInstructionsModal} style={styles.cancelButton}>
                  <CustomText text="Cancel" size="s" useTranslationText={false} style={styles.cancelButtonText} />
                </Pressable>
                <Pressable accessibilityRole="button" onPress={() => void saveInstructions()} style={styles.saveButton}>
                  <CustomText
                    text="Save Instructions"
                    size="s"
                    weight="bold"
                    useTranslationText={false}
                    style={styles.primaryButtonText}
                  />
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>
        <CustomModal
          visible={pendingAiGeneration !== null || isUploadOutcomeWarningOpen}
          onRequestClose={pendingAiGeneration !== null ? closeAiGenerationWarningModal : closeUploadOutcomeWarningModal}
          ariaLabel={
            pendingAiGeneration !== null
              ? "Confirm learning outcome generation"
              : "Confirm PDF or DOC outcome extraction"
          }
          style={styles.warningModal}>
          <View style={styles.warningModalHeader}>
            <View style={styles.warningModalHeading}>
              <WarningIcon color="#D97706" size={18} weight="fill" />
              <CustomText
                text={pendingAiGeneration !== null ? "Generate new learning outcomes?" : "Upload PDF or DOC?"}
                size="s"
                weight="bold"
                role="heading"
                aria-level="2"
                useTranslationText={false}
                style={styles.warningModalTitle}
              />
            </View>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={
                pendingAiGeneration !== null
                  ? "Close learning outcome generation warning"
                  : "Close PDF or DOC upload warning"
              }
              onPress={pendingAiGeneration !== null ? closeAiGenerationWarningModal : closeUploadOutcomeWarningModal}
              style={styles.warningModalCloseButton}>
              <XIcon color={colors.grey[500]} size={17} weight="bold" />
            </Pressable>
          </View>
          <CustomText
            text={
              pendingAiGeneration !== null
                ? `This will replace the ${outcomes.length} outcome${outcomes.length === 1 ? "" : "s"} currently shown. Do you want to proceed?`
                : `This will replace the ${outcomes.length} outcome${outcomes.length === 1 ? "" : "s"} currently shown. Do you want to proceed?`
            }
            size="s"
            useTranslationText={false}
            style={styles.warningModalText}
          />
          <View style={styles.warningModalActions}>
            <CustomButton
              text="Cancel"
              styleType="secondary"
              size="s"
              onPress={pendingAiGeneration !== null ? closeAiGenerationWarningModal : closeUploadOutcomeWarningModal}
              accessibilityLabel={
                pendingAiGeneration !== null
                  ? "Cancel learning outcome generation"
                  : "Cancel PDF or DOC outcome extraction"
              }
            />
            <CustomButton
              text={
                pendingAiGeneration !== null
                  ? isGeneratingOutcomes
                    ? "Generating..."
                    : "Generate"
                  : "Upload PDF or DOC"
              }
              styleType="primary"
              size="s"
              onPress={
                pendingAiGeneration !== null ? () => void confirmGenerateOutcomesWithAI() : confirmUploadPdfOrDoc
              }
              disabled={isGeneratingOutcomes}
              accessibilityLabel={
                pendingAiGeneration !== null
                  ? "Generate learning outcomes"
                  : "Proceed with PDF or DOC outcome extraction"
              }
            />
          </View>
        </CustomModal>
      </View>
    </View>
  );
}

const getMaterialSourceLabel = (material: IntakeMaterial): string =>
  material.sourceConnectorType
    ? `${CONNECTOR_SOURCE_LABELS[material.sourceConnectorType] ?? "connector"}`
    : "Uploaded file";

const getMaterialSummaryLabel = (material: IntakeMaterial): string => {
  const status = material.analysisStatus as IntakeAnalysisStatus | null;
  const hasSummary = Boolean(material.summary?.trim());
  if (status === "FAILED") return "Summary failed";
  if (status === "UNSUPPORTED") return "Summary unavailable";
  if (((status === "QUEUED" || status === "PROCESSING") && !hasSummary) || (!status && !hasSummary)) {
    return "Summary processing";
  }
  return hasSummary ? "Summary ready" : "Summary not generated";
};

const getMaterialSummaryText = (material: IntakeMaterial): string => {
  const status = material.analysisStatus as IntakeAnalysisStatus | null;
  const hasSummary = Boolean(material.summary?.trim());
  if (status === "FAILED" || status === "UNSUPPORTED") {
    return (
      material.analysisError?.trim() ||
      "There has been a problem with summary generation. You may try again. Failed summary generation does not block modernizer."
    );
  }
  if (((status === "QUEUED" || status === "PROCESSING") && !hasSummary) || (!status && !hasSummary)) {
    return "Summary is being processed, please be patient.";
  }
  if (!hasSummary) {
    return "There has been a problem with summary generation. You may try again. Failed summary generation does not block modernizer.";
  }
  return material.summary as string;
};

const canRetryMaterialSummary = (material: IntakeMaterial): boolean =>
  material.analysisStatus === "FAILED" ||
  (material.analysisStatus === "UNSUPPORTED" &&
    !material.analysisError?.toLowerCase().includes("upload a supported file type"));

const getMaterialSummaryUiStatus = (material: IntakeMaterial): "generating" | "not_generated" | "generated" => {
  const status = material.analysisStatus as IntakeAnalysisStatus | null;
  const hasSummary = Boolean(material.summary?.trim());
  if (status === "FAILED" || status === "UNSUPPORTED") return "not_generated";
  if (((status === "QUEUED" || status === "PROCESSING") && !hasSummary) || (!status && !hasSummary)) {
    return "generating";
  }
  return hasSummary ? "generated" : "not_generated";
};

function Panel({ children }: { children: React.ReactNode }) {
  return <View style={styles.panel}>{children}</View>;
}

function MaterialCardShell({ children }: { children: React.ReactNode }) {
  return <View style={styles.materialCard}>{children}</View>;
}

function RetentionButton({ label, onPress, selected }: { label: string; onPress: () => void; selected: boolean }) {
  const iconColor = selected ? "#1D4ED8" : "#64748B";
  const icon =
    label === "Retain" ? (
      <ArchiveIcon color={iconColor} size={13} strokeWidth={selected ? 2.5 : 2} />
    ) : (
      <TrashIcon color={iconColor} size={13} weight={selected ? "bold" : "regular"} />
    );

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${label} material after modernization`}
      onPress={onPress}
      style={[styles.retentionButton, selected && styles.retentionButtonSelected]}>
      {icon}
      <CustomText
        text={label}
        size="xs"
        weight={selected ? "bold" : "regular"}
        useTranslationText={false}
        style={selected ? styles.blueText : styles.retentionButtonText}
      />
    </Pressable>
  );
}

function SectionTitle({ title, optional }: { title: string; optional?: boolean }) {
  return (
    <View style={styles.sectionTitleRow}>
      <CustomText text={title} size="m" weight="bold" useTranslationText={false} />
      {optional ? <CustomText text="optional" size="s" useTranslationText={false} style={styles.panelHint} /> : null}
    </View>
  );
}

function IntakeTextArea({
  accessibilityLabel,
  compact,
  onChangeText,
  placeholder,
  value,
}: {
  accessibilityLabel: string;
  compact?: boolean;
  onChangeText: (value: string) => void;
  placeholder: string;
  value: string;
}) {
  return (
    <TextInput
      accessibilityLabel={accessibilityLabel}
      multiline
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor="#64748B"
      style={[styles.intakeTextArea, compact && styles.intakeTextAreaCompact]}
    />
  );
}

function GuidedTextPanel({
  accessibilityLabel,
  helperText,
  icon,
  onChangeText,
  placeholder,
  prompts,
  title,
  value,
}: {
  accessibilityLabel: string;
  helperText: string;
  icon: React.ReactNode;
  onChangeText: (value: string) => void;
  placeholder: string;
  prompts: string[];
  title: string;
  value: string;
}) {
  return (
    <Panel>
      <SectionTitle title={title} optional />
      <CustomText text={helperText} size="xs" useTranslationText={false} style={styles.guidedHelperText} />
      <View style={styles.guidedPromptBox}>
        <View style={styles.guidedPromptHeader}>
          {icon}
          <CustomText
            text="CONSIDER ANSWERING"
            size="xs"
            weight="bold"
            useTranslationText={false}
            style={styles.guidedPromptTitle}
          />
        </View>
        <View style={styles.guidedPromptList}>
          {prompts.map((prompt) => (
            <View key={prompt} style={styles.guidedPromptItem}>
              <CustomText text="•" size="xs" useTranslationText={false} style={styles.guidedBullet} />
              <CustomText text={prompt} size="xs" useTranslationText={false} style={styles.guidedPromptText} />
            </View>
          ))}
        </View>
      </View>
      <IntakeTextArea
        accessibilityLabel={accessibilityLabel}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
      />
    </Panel>
  );
}

function IconButton({
  compact,
  icon,
  label,
  onPress,
  tooltipLabel,
}: {
  compact?: boolean;
  icon: React.ReactNode;
  label: string;
  onPress: () => void;
  tooltipLabel?: string;
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onPress={onPress}
      style={[styles.iconButton, compact && styles.iconButtonCompact]}>
      {icon}
      {compact ? null : <CustomText text={label} size="xs" useTranslationText={false} style={styles.iconButtonText} />}
      {compact && isHovered && tooltipLabel ? (
        <View style={styles.iconTooltip}>
          <CustomText text={tooltipLabel} size="xs" useTranslationText={false} style={styles.iconTooltipText} />
        </View>
      ) : null}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  shell: { flex: 1, backgroundColor: "#F8FAFC" },
  topBar: {
    height: 44,
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
    backgroundColor: "#FFFFFF",
    flexDirection: "row",
    alignItems: "center",
  },
  exitButton: {
    height: 44,
    paddingHorizontal: 20,
    borderRightWidth: 1,
    borderRightColor: "#E2E8F0",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  exitText: { color: "#334155" },
  topBarTitle: { color: "#0F172A", marginLeft: 16 },
  scroll: { flex: 1 },
  content: {
    width: "50%",
    maxWidth: "100%",
    alignSelf: "center",
    paddingHorizontal: 16,
    paddingTop: 28,
    paddingBottom: 128,
    gap: 20,
  },
  hero: { gap: 8 },
  eyebrow: { color: "#1D4ED8", letterSpacing: 0.4 },
  title: { color: "#0F172A" },
  subtitle: { color: "#475569" },
  panel: { backgroundColor: "#FFFFFF", borderWidth: 1, borderColor: "#E2E8F0", borderRadius: 12, padding: 20, gap: 14 },
  panelHint: { color: "#64748B", lineHeight: 20 },
  iconButton: { flexDirection: "row", alignItems: "center", gap: 4, paddingVertical: 4 },
  iconButtonCompact: {
    width: 28,
    height: 28,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 0,
    position: "relative",
  },
  iconButtonText: { color: "#334155" },
  iconTooltip: {
    position: "absolute",
    right: -12,
    top: -42,
    minWidth: 132,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 6,
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 10,
    paddingVertical: 8,
    shadowColor: "#0F172A",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    zIndex: 20,
    elevation: 6,
  },
  iconTooltipText: { color: "#1F2937", whiteSpace: "nowrap" as never },
  primaryButtonText: { color: "#FFFFFF" },
  disabledButton: { opacity: 0.45 },
  accordionShell: { backgroundColor: "#FFFFFF", borderWidth: 1, borderColor: "#E2E8F0", borderRadius: 12 },
  accordionHeader: { padding: 16, flexDirection: "row", alignItems: "center", gap: 12 },
  advancedIcon: {
    width: 34,
    height: 34,
    borderRadius: 8,
    backgroundColor: "#F1F5F9",
    alignItems: "center",
    justifyContent: "center",
  },
  accordionTitle: { flex: 1, gap: 4 },
  advancedStack: { gap: 20 },
  materialCard: {
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    overflow: "visible",
  },
  materialCardContent: {
    padding: 16,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  materialIcon: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: "#FEF2F2",
    alignItems: "center",
    justifyContent: "center",
  },
  materialCardMain: { flex: 1, gap: 13 },
  materialTitleRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  materialTitleBlock: { flex: 1, gap: 3 },
  materialActions: { flexDirection: "row", alignItems: "center", gap: 2, zIndex: 10 },
  materialSummaryRow: { flexDirection: "row", alignItems: "center", gap: 8 },
  materialSummaryText: { color: "#334155", lineHeight: 18 },
  materialMetadataRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  materialMetadataSummaryText: { lineHeight: 16 },
  summaryReadyText: { color: "#166534" },
  materialNoteRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    backgroundColor: "#FAF5FF",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  materialNote: { flex: 1, color: "#334155", lineHeight: 18 },
  retentionRow: {
    minHeight: 36,
    borderTopWidth: 1,
    borderTopColor: "#E2E8F0",
    backgroundColor: "#F8FAFC",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    gap: 6,
  },
  retentionButton: {
    borderWidth: 1,
    borderColor: "#CBD5E1",
    borderRadius: 999,
    paddingHorizontal: 9,
    paddingVertical: 3,
    backgroundColor: "#FFFFFF",
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  retentionButtonSelected: {
    borderColor: "#BFDBFE",
    backgroundColor: "#DBEAFE",
  },
  retentionButtonText: { color: "#475569" },
  sectionTitleRow: { flexDirection: "row", alignItems: "baseline", gap: 4 },
  guidedHelperText: {
    color: "#0F1F44",
    marginTop: -8,
    lineHeight: 16,
  },
  guidedPromptBox: {
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 6,
    backgroundColor: "#F8FAFC",
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 7,
  },
  guidedPromptHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  guidedPromptTitle: {
    color: "#003CB3",
    fontSize: 9,
    letterSpacing: 0.2,
  },
  guidedPromptList: {
    gap: 5,
    paddingLeft: 19,
  },
  guidedPromptItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  guidedBullet: {
    color: "#0F1F44",
    lineHeight: 12,
  },
  guidedPromptText: {
    flex: 1,
    color: "#0F1F44",
    fontSize: 10,
    lineHeight: 12,
  },
  signatureOtherLabel: {
    color: "#475569",
    fontSize: 10,
    letterSpacing: 0.4,
    marginBottom: -6,
  },
  intakeTextArea: {
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 6,
    paddingHorizontal: 11,
    paddingVertical: 10,
    minHeight: 132,
    color: "#0F172A",
    fontSize: 12,
    lineHeight: 19,
    textAlignVertical: "top",
    outlineStyle: "none" as never,
  },
  intakeTextAreaCompact: {
    minHeight: 78,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(24, 34, 48, 0.78)",
    alignItems: "center",
    justifyContent: "center",
    padding: spacing.sm,
  },
  instructionsModal: {
    width: 588,
    maxWidth: "100%",
    borderRadius: radii.lg - spacing.xxs,
    backgroundColor: colors.white,
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.xl,
    paddingBottom: spacing.lg + spacing.xxs,
    gap: spacing.lg,
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: spacing.sm,
  },
  modalTitle: { color: colors.grey[700], marginBottom: spacing.xxs },
  warningModal: {
    maxWidth: 480,
    borderRadius: radii.lg,
  },
  warningModalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.xs,
  },
  warningModalHeading: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    flex: 1,
  },
  warningModalTitle: {
    color: colors.grey[800],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    flex: 1,
  },
  warningModalCloseButton: {
    width: spacing.xl,
    height: spacing.xl,
    alignItems: "center",
    justifyContent: "center",
  },
  warningModalText: {
    color: colors.grey[700],
    lineHeight: typographyScale.textSm.lineHeight,
    marginTop: spacing.sm,
  },
  warningModalActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: spacing.xs + spacing.xxs,
    marginTop: spacing.lg,
  },
  closeButton: {
    width: spacing.xl,
    height: spacing.xl,
    alignItems: "center",
    justifyContent: "center",
  },
  closeButtonText: { color: colors.grey[500] },
  instructionsTextArea: {
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: radii.md,
    height: 150,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    color: colors.grey[900],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    textAlignVertical: "top",
    outlineStyle: "none" as never,
  },
  instructionsTextAreaFocused: {
    borderWidth: 2,
    borderColor: colors.brand[400],
    boxShadow: `0 0 0 1px ${colors.brand[400]}` as never,
  },
  modalFooter: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: spacing.xs + spacing.xxs,
  },
  cancelButton: {
    minHeight: 42,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: radii.sm,
    paddingHorizontal: spacing.sm + spacing.xxs,
    alignItems: "center",
    justifyContent: "center",
  },
  cancelButtonText: { color: colors.grey[700] },
  saveButton: {
    minHeight: 42,
    borderRadius: radii.sm,
    backgroundColor: colors.brand[500],
    paddingHorizontal: spacing.sm + spacing.xs,
    alignItems: "center",
    justifyContent: "center",
  },
  tagGrid: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  tag: { borderWidth: 1, borderColor: "#CBD5E1", borderRadius: 999, paddingHorizontal: 10, paddingVertical: 6 },
  tagSelected: { backgroundColor: "#EFF6FF", borderColor: "#93C5FD" },
  tagText: { color: "#334155" },
  blueText: { color: "#1D4ED8" },
  errorText: { color: "#B42318" },
  linkButton: { alignSelf: "flex-start", paddingVertical: 8 },
  stickyFooter: {
    height: 56,
    borderTopWidth: 1,
    borderTopColor: "#E2E8F0",
    backgroundColor: "#FFFFFF",
    alignItems: "center",
    justifyContent: "center",
  },
  footerContent: {
    width: 586,
    maxWidth: "100%",
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 24,
  },
  footerNote: {
    width: 520,
    maxWidth: "70%",
    color: "#64748B",
    lineHeight: 18,
  },
  modernizeButton: {
    paddingHorizontal: 10,
    paddingVertical: 10,

    borderRadius: 6,
    backgroundColor: "#3E68FE",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  page: {
    flex: 1,
    backgroundColor: "#F8FAFC",
    ...(Platform.OS === "web"
      ? {
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          height: "100vh",
          overflow: "hidden",
          display: "flex",
        }
      : {}),
  },
});

export default CourseModernizerIntakeView;
